import React, { FC, useCallback } from 'react';

import { CustomEventNames, EventTypes, logEvent } from 'features/analytics';
import { IReorderEntry } from 'features/menu/hooks/types';
import { useReorderEntries } from 'features/menu/hooks/use-reorder-entries';
import { IFavoriteCardProps } from 'features/menu/pages/favorites-page/favorites-card-list/favorite-card/types';
import { RecentOrderCard } from 'features/menu/pages/recent-items-page/recent-order-card-list/recent-order-card-list-item';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import ItemActions from 'components/item-actions';
import { ListItemGroup } from 'components/ui/list-item-group';
import useFoodware from 'hooks/use-foodware';
import { isCartEntryReorderable } from 'hooks/use-reorderable-orders/utils';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { useReorder } from 'state/order/hooks/use-reorder';
import { getCartEntryRef } from 'utils/menu/get-cart-entry-ref';
import { routes } from 'utils/routing';

export const FavoriteCard: FC<React.PropsWithChildren<IFavoriteCardProps>> = ({
  id,
  entries,
  name,
  favoriteRef,
  onEditFavorite,
  onRemoveFavorite,
}) => {
  const favoriteOrder = {
    rbiOrderId: id,
    cart: {
      cartEntries: entries,
      serviceMode: null,
    },
  };

  const { handleReorderClick } = useReorder(favoriteOrder, routes.menu);
  const { reorderEntriesMap, checkingAvailability, hasSomeAvailableEntry } = useReorderEntries(
    favoriteOrder?.cart.cartEntries
  );

  // Quick-favorites (saved via the heart icon on a single item, `FavoriteEntryButton`) don't
  // have a user-authored title — `name` is just a copy of the item's name captured at save
  // time, so it goes stale after a language switch. A single-item order can also be saved
  // through the "Save Favorite Order" modal with a genuine custom title (e.g. "Lunch"), and
  // there's no dedicated flag distinguishing the two. But the two flows set `ref` differently:
  // quick-favorites store `getCartEntryRef(cartEntry)`, order-favorites store `rbiOrderId`. So
  // recomputing the entry's own ref and comparing it to the stored `ref` reliably identifies a
  // quick-favorite, even if the custom title happens to match the item's name (e.g. "Whopper").
  const singleEntry = entries.length === 1 ? entries[0] : undefined;
  const isQuickFavorite =
    !!singleEntry && !!favoriteRef && getCartEntryRef(singleEntry) === favoriteRef;
  const singleEntryLiveName = isQuickFavorite
    ? reorderEntriesMap[singleEntry.lineId]?.frontendEntry?.name
    : undefined;
  const header = singleEntryLiveName || name || '';

  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const { isFoodware } = useFoodware();

  const handleReorder = useCallback(() => {
    for (const entry of favoriteOrder.cart.cartEntries) {
      logEvent(CustomEventNames.ADD_FAVORITE_TO_CART, EventTypes.UserContent, {
        'Product Name': entry?.name,
        'User Location': 'Favorites Tab',
      });
    }
    handleReorderClick();
  }, [favoriteOrder.cart.cartEntries, handleReorderClick]);

  if (!entries.length) {
    return null;
  }

  const recentOrderCardsFromFavorites = entries.reduce((acc: React.ReactNode[], entry) => {
    if (!isCartEntryReorderable(entry, sanityIdsExcludedFromReordering, isFoodware)) {
      return acc;
    }

    acc.push(
      <RecentOrderCard
        key={entry?.lineId}
        cartEntry={entry}
        mappedEntry={reorderEntriesMap[entry.lineId] ?? ({} as IReorderEntry)}
        checkingAvailability={checkingAvailability}
        isOnFavoriteSection
      />
    );

    return acc;
  }, []);

  return (
    <ListItemGroup
      header={header}
      headerAction={
        <ActionButton
          testID="reorder-button"
          onPress={handleReorder}
          disabled={!hasSomeAvailableEntry}
          size={ActionButtonSizes.SMALL}
        >
          {t('orderNow')}
        </ActionButton>
      }
      items={recentOrderCardsFromFavorites}
      footer={
        <ItemActions
          onEdit={() => onEditFavorite(id ?? '')}
          onRemove={() => onRemoveFavorite(id ?? '')}
        />
      }
      key={id}
    />
  );
};
